/**
 * Contact Service
 * Handles contact form submission to the API
 */

import { environment } from '../environments/environment.js';
import type {
  ContactRequest,
  ContactSuccessResponse,
  ContactErrorResponse,
} from '../types/contact.js';

/**
 * Singleton service for contact form submission
 */
class ContactService {
  private static instance: ContactService;
  private readonly baseUrl = `${environment.api}/${environment.endpoints.contact}`;

  private constructor() {}

  /**
   * Get singleton instance
   */
  static getInstance(): ContactService {
    if (!ContactService.instance) {
      ContactService.instance = new ContactService();
    }
    return ContactService.instance;
  }

  /**
   * Submit contact form to API
   */
  async submitContact(
    data: ContactRequest
  ): Promise<ContactSuccessResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      const responseData = await response.json();

      if (!response.ok) {
        // Handle error response
        const errorResponse = responseData as ContactErrorResponse;
        throw new Error(
          errorResponse.error?.message || `Request failed with status ${response.status}`
        );
      }

      // Validate success response structure
      const successResponse = responseData as ContactSuccessResponse;
      if (successResponse.status !== 'success') {
        throw new Error('Invalid response format from server');
      }

      return successResponse;
    } catch (error) {
      console.error('Error submitting contact form:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const contactService = ContactService.getInstance();
